/**
 * security-company-landing-page.ts
 *
 * Structured Institutional Security Landing Page Definition (Uganda Sovereign Asset Protection):
 * Implements the 7-section Information Architecture & CMS Component Schema with Rich Verified Media:
 * 1. Hero Banner (Value proposition, trust badges, dual CTAs, hero photo)
 * 2. Paradigm Shift Comparison Matrix (Traditional Sentry vs. Integrated Modern System)
 * 3. 5 Strategic Service Pillars (Vault Custody, CIT Bullion, Smart AI Tech, Industrial, Risk Advisory) with media photos
 * 4. Technology & AI Architecture (24/7 SOC, AI Video Edge, CIT GPS, Biometric Access) with technical diagrams
 * 5. Specialized Industry Matrix (Mining/Gold, Oil & Gas, Banking, Infrastructure, Corporate, Private Vault)
 * 6. Trust, Governance & Chain-of-Custody (Vetting, Chain of Custody, Confidentiality, Compliance)
 * 7. Segmented Lead Capture Form & Kampala HQ Information
 */

import type { CmsPageTree } from '../cms-contracts.js';

export function createSecurityCompanyLandingPageTree(): CmsPageTree {
  return {
    rootVersion: 1,
    sections: [
      // Section 1: Hero Banner
      {
        sectionId: 'sec-hero',
        layout: 'full_width',
        themeVariant: 'inverse',
        blocks: [
          {
            blockId: 'blk-hero-01',
            type: 'hero_banner',
            schemaVersion: 1,
            sortOrder: 1,
            isVisible: true,
            payload: {
              eyebrow: 'Institutional Security & Asset Protection',
              title: 'Protecting What Matters. Securing Uganda’s Future.',
              description:
                "From high-security private vaults and cash logistics to AI-assisted surveillance and industrial asset protection—engineered for Uganda's rapidly expanding economic frontiers.",
              imageUrl: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&auto=format&fit=crop&q=80',
              imageAltText: 'Sovereign Asset Defense Operations Center & Tactical Security Command in Kampala',
              primaryCta: {
                label: 'Request Security Consultation',
                url: '#lead-inquiry-form',
                target: '_self',
                isExternal: false,
              },
              secondaryCta: {
                label: 'Explore Private Vault Custody',
                url: '#service-vault-custody',
                target: '_self',
                isExternal: false,
              },
              overlay: 'dark',
              alignment: 'left',
              trustBadges: [
                'Bank-Grade Private Vaults',
                'Real-Time SOC & AI Analytics',
                'Regulated Cash-in-Transit (CIT)',
                'High-Value Industrial Protection',
              ],
            },
          },
        ],
      },

      // Section 2: Paradigm Shift (Traditional vs. Modern Security)
      {
        sectionId: 'sec-paradigm-shift',
        layout: 'contained',
        themeVariant: 'default',
        blocks: [
          {
            blockId: 'blk-comparison-01',
            type: 'comparison_matrix',
            schemaVersion: 1,
            sortOrder: 2,
            isVisible: true,
            payload: {
              sectionHeading: 'Moving Beyond the Traditional Security Guard Model',
              sectionSubheading:
                'Modern threat mitigation requires continuous data, intelligence, and immediate operational response—not just a gate and a guard.',
              imageUrl: 'https://images.unsplash.com/photo-1508873696983-2df57046475a?w=800&auto=format&fit=crop&q=80',
              legacyTitle: 'Traditional Security Model',
              legacyPoints: [
                'Unconnected on-site sentries with high turnover',
                'Passive CCTV recording with reactive post-incident review',
                'Manual paper visitor logs and blind spots across perimeters',
                'Zero telemetry, single point of human failure, unverified claims',
              ],
              modernTitle: 'Integrated Security Platform',
              modernPoints: [
                'Vetted, disciplined personnel connected to 24/7 Command SOC',
                'AI video analytics with instant automated perimeter breach alerts',
                'Biometric dual-custody access control with immutable audit trails',
                'Heavily armored CIT logistics with real-time GPS route telemetry',
              ],
            },
          },
        ],
      },

      // Section 3: Core Service Pillars
      {
        sectionId: 'sec-service-pillars',
        layout: 'contained',
        themeVariant: 'default',
        blocks: [
          {
            blockId: 'blk-services-01',
            type: 'service_catalog',
            schemaVersion: 1,
            sortOrder: 3,
            isVisible: true,
            payload: {
              sectionHeading: 'Five Strategic Service Pillars',
              sectionSubheading:
                'End-to-end security architectures engineered for institutional enterprises, financial houses, and private estate assets.',
              displayMode: 'tabs',
              services: [
                {
                  serviceId: 'service-vault-custody',
                  serviceTitle: 'Secure Custody & Private Vaults',
                  categorySlug: 'custody',
                  targetAudience: 'HNWIs, Legal Firms, Corporations, Financial Institutions',
                  shortDescription:
                    'Ultra-secure storage facility engineered for land titles, corporate records, precious assets, and confidential physical media.',
                  imageUrl: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Bank-Grade Private Vault Custody & Safe Deposit Lockers',
                  featureList: [
                    'Land title and property deed safekeeping',
                    'High-security private client lockers & corporate vaults',
                    'Chain-of-custody tracking with strict biometric access control',
                    'Climate-controlled, fireproof, and audit-logged document archives',
                  ],
                  iconName: 'vault',
                },
                {
                  serviceId: 'service-cit-bullion',
                  serviceTitle: 'Cash-in-Transit (CIT) & Bullion Logistics',
                  categorySlug: 'cit-logistics',
                  targetAudience: 'Banks, Gold Refineries, Retail Consortia, Mining Operations',
                  shortDescription:
                    'Heavily armored, GPS-monitored cash and precious metal logistics across Uganda.',
                  imageUrl: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Armored Cash-in-Transit (CIT) Transport Fleet with Route GPS',
                  featureList: [
                    'Bank-to-business and business-to-bank daily collections',
                    'Precious metal (gold/bullion) secure transit under strict regulatory compliance',
                    'Real-time GPS tracking with direct 24/7 SOC route monitoring',
                    'Dynamic route risk profiling and rapid-response escort protocols',
                  ],
                  iconName: 'armored-truck',
                },
                {
                  serviceId: 'service-ai-surveillance',
                  serviceTitle: 'AI-Assisted Surveillance & Smart Tech',
                  categorySlug: 'surveillance',
                  targetAudience: 'Commercial Complexes, Warehouses, Logistics Hubs, Estates',
                  shortDescription:
                    'Integrated surveillance infrastructure replacing passive recording with active threat intelligence.',
                  imageUrl: 'https://images.unsplash.com/photo-1508873696983-2df57046475a?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Central Command Security Operations Center (SOC) Video Analytics',
                  featureList: [
                    'AI video analytics (perimeter breach, anomaly detection, vehicle recognition)',
                    '24/7 centralized Security Operations Centre (SOC) monitoring',
                    'Biometric access control & smart card credentials',
                    'Automated alert escalation and digital incident audit logs',
                  ],
                  iconName: 'cctv-ai',
                },
                {
                  serviceId: 'service-industrial-security',
                  serviceTitle: 'High-Value Industry Security',
                  categorySlug: 'industrial',
                  targetAudience: 'Oil & Gas, Mining, Energy, Infrastructure & EPC Contractors',
                  shortDescription:
                    'Tailored multi-layered security architectures built for Uganda’s critical development sectors.',
                  imageUrl: 'https://images.unsplash.com/photo-1578328819058-b69f3a3b0f6b?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Industrial Mining & Oil Concession Perimeter Defense',
                  featureList: [
                    'Perimeter protection for remote drilling, extraction, and mining operations',
                    'Asset and supply-chain logistics protection for infrastructure projects',
                    'Compliance-driven security protocols for multinational joint ventures',
                    'Site access governance and hostile-environment risk management',
                  ],
                  iconName: 'industrial-plant',
                },
                {
                  serviceId: 'service-risk-advisory',
                  serviceTitle: 'Threat Assessment & Risk Advisory',
                  categorySlug: 'advisory',
                  targetAudience: 'Corporate Boards, Facility Managers, Investors Entering Uganda',
                  shortDescription:
                    'Proactive risk identification before deployment or operational rollout.',
                  imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Confidential Facility Threat Assessment & Executive Risk Auditing',
                  featureList: [
                    'Physical facility vulnerability & penetration audits',
                    'Internal fraud and personnel exposure assessments',
                    'Business continuity and emergency evacuation planning',
                    'Supply-chain and transportation risk analysis',
                  ],
                  iconName: 'shield-alert',
                },
              ],
            },
          },
        ],
      },

      // Section 4: AI & Technology Engine
      {
        sectionId: 'sec-tech-engine',
        layout: 'contained',
        themeVariant: 'inverse',
        blocks: [
          {
            blockId: 'blk-tech-01',
            type: 'tech_engine',
            schemaVersion: 1,
            sortOrder: 4,
            isVisible: true,
            payload: {
              sectionHeading: 'Human Intelligence + Artificial Intelligence = Smarter Security',
              coreMessage:
                'Our integrated operations eliminate blindspots and human fatigue by fusing edge AI video algorithms with 24/7 centralized SOC dispatch.',
              technologies: [
                {
                  featureTitle: 'AI Video Edge & Threat Isolation',
                  techType: 'video_ai',
                  description:
                    'Edge AI algorithms analyze camera streams in real-time, instantly isolating unauthorized perimeter breaches, loitering, and irregular vehicle patterns.',
                  metricBadge: '< 2.4s Incident Detection',
                  imageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'AI Video Edge Threat Isolation Diagram',
                  bulletPoints: [
                    'License Plate Recognition (ANPR)',
                    'Thermal perimeter fence breach detection',
                    'Automated anomaly tagging',
                  ],
                },
                {
                  featureTitle: 'CIT & Asset Telemetry GPS',
                  techType: 'gps',
                  description:
                    'Armored transports transmit continuous encrypted telemetry to the Kampala SOC with automated geofence variance alarms.',
                  metricBadge: '100% Real-Time Fleet Tracking',
                  imageUrl: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'CIT Fleet GPS Route Telemetry Mapping',
                  bulletPoints: [
                    'Sub-second route divergence detection',
                    'Panic sensor & remote engine immobilizer',
                    'Dynamic traffic-risk routing',
                  ],
                },
                {
                  featureTitle: 'Biometric Access & Dual-Custody',
                  techType: 'biometric',
                  description:
                    'Vault facilities and sensitive data rooms enforce mandatory dual-custody authorization with cryptographic audit logging.',
                  metricBadge: 'Zero Single-Point Access',
                  imageUrl: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&auto=format&fit=crop&q=80',
                  imageAltText: 'Biometric Vault Access Authentication Terminal',
                  bulletPoints: [
                    'Multi-modal biometric fingerprint & facial scans',
                    'Dual-key custodian check-in',
                    'Tamper-evident access ledgers',
                  ],
                },
                {
                  featureTitle: '24/7 Command SOC Dispatch',
                  techType: 'soc',
                  description:
                    'Central command facility in Kampala operating 24/7/365 with direct communication links to national rapid-response units.',
                  metricBadge: '24/7/365 Active Command',
                  imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80',
                  imageAltText: '24/7 Kampala Command Center SOC Dispatch Desk',
                  bulletPoints: [
                    'Unified dashboard telemetry',
                    'Direct law enforcement & armed escort coordination',
                    'Automated client incident reporting',
                  ],
                },
              ],
            },
          },
        ],
      },

      // Section 5: Specialized Industry Matrix
      {
        sectionId: 'sec-industry-matrix',
        layout: 'contained',
        themeVariant: 'default',
        blocks: [
          {
            blockId: 'blk-industry-01',
            type: 'industry_matrix',
            schemaVersion: 1,
            sortOrder: 5,
            isVisible: true,
            payload: {
              sectionHeading: 'Specialized Industry Solutions',
              sectionSubheading:
                'Tailored security frameworks aligned with the operational realities and regulatory standards of Uganda’s major sectors.',
              industries: [
                {
                  industryName: 'Mining & Gold Refineries',
                  slug: 'mining-gold',
                  iconName: 'gem',
                  riskProfileSummary:
                    'Bullion logistics, on-site physical perimeter security, and raw mineral smuggling mitigation.',
                  applicableServices: ['CIT Bullion Logistics', 'Armored Guarding', 'Vault Custody'],
                },
                {
                  industryName: 'Oil & Gas Sector',
                  slug: 'oil-gas',
                  iconName: 'flame',
                  riskProfileSummary:
                    'Perimeter monitoring, pipeline infrastructure protection, base camps, and hostile environment safety.',
                  applicableServices: ['AI Surveillance', 'Perimeter Defense', 'Emergency Evacuation'],
                },
                {
                  industryName: 'Banking & Financial Institutions',
                  slug: 'banking-finance',
                  iconName: 'landmark',
                  riskProfileSummary:
                    'Daily CIT branch routes, vault document custody, access control, and ATM replenish escorts.',
                  applicableServices: ['CIT Operations', 'Vault Document Custody', 'SOC Monitoring'],
                },
                {
                  industryName: 'Infrastructure & EPC Contractors',
                  slug: 'infrastructure-epc',
                  iconName: 'crane',
                  riskProfileSummary:
                    'Heavy equipment yards, supply chain transport corridors, and site material loss prevention.',
                  applicableServices: ['Access Governance', 'Supply Chain Security', 'Asset GPS'],
                },
                {
                  industryName: 'Corporate, Embassies & Legal',
                  slug: 'corporate-legal',
                  iconName: 'briefcase',
                  riskProfileSummary:
                    'Confidential archive custody, executive close protection, and secure data rooms.',
                  applicableServices: ['Title Deed Safekeeping', 'Executive Escorts', 'Facility Audits'],
                },
                {
                  industryName: 'Private Clients & Family Estates',
                  slug: 'private-clients',
                  iconName: 'home-shield',
                  riskProfileSummary:
                    'Private lockers, land title preservation, precious collectibles, and residential smart security.',
                  applicableServices: ['Private Vaults', 'Estate AI Security', 'Confidential Custody'],
                },
              ],
            },
          },
        ],
      },

      // Section 6: Trust, Governance & Chain-of-Custody
      {
        sectionId: 'sec-trust-governance',
        layout: 'contained',
        themeVariant: 'default',
        blocks: [
          {
            blockId: 'blk-trust-01',
            type: 'trust_audit',
            schemaVersion: 1,
            sortOrder: 6,
            isVisible: true,
            payload: {
              headline: 'Trust Is Our Primary Asset.',
              sectionHeading: 'Trust Is Our Primary Asset.',
              body: 'When protecting land titles, national infrastructure, or corporate treasury, trust must be structural, auditable, and absolute.',
              sectionSubheading:
                'When protecting land titles, national infrastructure, or corporate treasury, trust must be structural, auditable, and absolute.',
              pillars: [
                {
                  title: 'Rigorous Background Vetting',
                  pillarTitle: 'Rigorous Background Vetting',
                  standardBadge: '100% Certified Operatives',
                  verificationStandard: '100% Certified Operatives',
                  description:
                    'Multi-tier background checks, psychological evaluations, and continuous biometric identity verification for all security operatives.',
                },
                {
                  title: 'Strict Chain of Custody',
                  pillarTitle: 'Strict Chain of Custody',
                  standardBadge: 'Cryptographic Audit Trail',
                  verificationStandard: 'Cryptographic Audit Trail',
                  description:
                    'Dual-custody handover protocols, digital timestamps, and segregated duties across every vault movement and armored dispatch.',
                },
                {
                  title: 'Confidentiality by Design',
                  pillarTitle: 'Confidentiality by Design',
                  standardBadge: 'Zero-Knowledge Privacy',
                  verificationStandard: 'Zero-Knowledge Privacy',
                  description:
                    'Ironclad Non-Disclosure Agreements, partitioned data records, and zero unauthorized exposure of private client vault holdings.',
                },
                {
                  title: 'Continuous Compliance Audits',
                  pillarTitle: 'Continuous Compliance Audits',
                  standardBadge: 'ISO 27001 & Regulatory Aligned',
                  verificationStandard: 'ISO 27001 & Regulatory Aligned',
                  description:
                    'Regular internal stress-testing, physical penetration audits, and full compliance with Ugandan security regulatory authorities.',
                },
              ],
            },
          },
        ],
      },

      // Section 7: Lead Capture & Contact
      {
        sectionId: 'sec-lead-capture',
        layout: 'contained',
        themeVariant: 'inverse',
        blocks: [
          {
            blockId: 'blk-lead-01',
            type: 'lead_capture_form',
            schemaVersion: 1,
            sortOrder: 7,
            isVisible: true,
            payload: {
              formId: 'form-uganda-lead-consultation',
              heading: 'Secure Your Operations & Assets',
              headline: 'Secure Your Operations & Assets',
              description: 'Speak with our Kampala-based security and asset custody specialists for a confidential risk evaluation.',
              subheadline:
                'Speak with our Kampala-based security and asset custody specialists for a confidential risk evaluation.',
              allowedFields: ['name', 'email', 'phone', 'organization', 'message', 'area_of_inquiry'],
              sectorDropdownOptions: [
                'Private Vault Custody',
                'Cash-in-Transit (CIT) & Bullion Logistics',
                'AI Surveillance & Smart Security',
                'Industrial & Mining Security',
                'Threat Assessment & Risk Advisory',
              ],
              purpose: 'institutional_assessment',
              consentPolicy: {
                required: true,
                legalBasis: 'explicit_consent',
                policyVersion: 'v1.0',
                disclosureText: 'I consent to confidential communication regarding institutional security under strict Non-Disclosure protocols.',
                doubleOptInRequired: false,
              },
              privacyNotice:
                'I consent to confidential communication regarding institutional security under strict Non-Disclosure protocols.',
              submitButtonLabel: 'Request Confidential Assessment',
              successMessage: 'Thank you for your inquiry. Our Kampala Command Center will reach out under confidential protocols.',
              formFields: [
                { fieldId: 'fullName', label: 'Full Name', fieldType: 'text', isRequired: true, placeholder: 'e.g. Director Sarah Mukasa' },
                { fieldId: 'companyName', label: 'Company / Institution', fieldType: 'text', isRequired: false, placeholder: 'e.g. Uganda Mining Consortium' },
                { fieldId: 'email', label: 'Official Email Address', fieldType: 'email', isRequired: true, placeholder: 's.mukasa@institution.ug' },
                { fieldId: 'phone', label: 'Phone Number', fieldType: 'tel', isRequired: true, placeholder: '+256 700 000 000' },
                {
                  fieldId: 'serviceInterest',
                  label: 'Area of Inquiry',
                  fieldType: 'select',
                  isRequired: true,
                  options: [
                    'Private Vault Custody',
                    'Cash-in-Transit (CIT) & Bullion Logistics',
                    'AI Surveillance & Smart Security',
                    'Industrial & Mining Security',
                    'Threat Assessment & Risk Advisory',
                  ],
                },
                { fieldId: 'message', label: 'Operational Scope / Notes', fieldType: 'textarea', isRequired: false, placeholder: 'Brief overview of asset protection or logistics requirements...' },
              ],
              officeLocations: [
                {
                  city: 'Kampala Headquarters & SOC',
                  address: 'Kampala, Uganda',
                  contactEmail: 'inquiries@sovereign-security.ug',
                  phone: '+256 414 000 000',
                  operationalHours: '24/7 Security Operations Centre (SOC)',
                },
              ],
            },
          },
        ],
      },
    ],
  };
}

